/**
 * Healthcheck & Alerting (monitoring) — shared data-layer infra.
 *
 * Backend is a dedicated Go service (`healthcheckApi`, base already includes
 * `/api/v1`). It speaks the datacentral envelope convention:
 *   - object: `{ message, result }`
 *   - array:  `{ message?, results, pagination }`
 *   - error:  `{ success: false, error: { code, message } }`
 *
 * Resources cascade: Watchpoint (monitor) → Alarm (rule) → AlarmEvent (incident),
 * with Channel/Subscriber as notification destinations. Watchpoints are scoped by
 * `project_id`; channels/events are tenant-scoped (from the JWT).
 */
import type { AxiosResponse } from 'axios';
import { healthcheckApi } from '@/api/client';

export { healthcheckApi };

// ---- Envelope ------------------------------------------------------------

export interface ObjectEnvelope<T> {
  message?: string;
  result: T;
}

export interface ListPagination {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  page_next: number;
  page_prev: number;
}

export interface ArrayEnvelope<T> {
  message?: string;
  results: T[];
  pagination?: ListPagination;
}

/** Return the inner `result` of an object response (falls back to the raw body). */
export function unwrap<T>(res: AxiosResponse<ObjectEnvelope<T> | T>): T {
  const body = res.data as unknown;
  if (body && typeof body === 'object' && 'result' in (body as Record<string, unknown>)) {
    return (body as ObjectEnvelope<T>).result;
  }
  return body as T;
}

export interface ListResult<T> {
  items: T[];
  pagination?: ListPagination;
}

/** Return `{ items, pagination }` from an array response (tolerant of shapes). */
export function unwrapList<T>(res: AxiosResponse<ArrayEnvelope<T> | T[]>): ListResult<T> {
  const body = res.data as unknown;
  if (Array.isArray(body)) return { items: body as T[] };
  const env = body as ArrayEnvelope<T>;
  return { items: env?.results ?? [], pagination: env?.pagination };
}

/**
 * Pull a human-readable error message from a healthcheck API error.
 * BE error envelope: `{ success: false, error: { code, message } }`.
 * Falls back through axios message then `fallback`.
 */
export function hcExtractError(e: unknown, fallback = 'Request failed'): string {
  if (e && typeof e === 'object') {
    const axErr = e as {
      response?: { data?: { error?: { message?: string }; message?: string } };
      message?: string;
    };
    const beMsg = axErr.response?.data?.error?.message ?? axErr.response?.data?.message;
    if (beMsg) return beMsg;
    if (axErr.message) return axErr.message;
  }
  return fallback;
}

/** Stable query-string fragment for keys/requests (sorted, no `qs` dep). */
export function toQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    if (v === undefined || v === null || v === '') continue;
    sp.append(k, String(v));
  }
  return sp.toString();
}

// ---- Domain types (json field names match the Go DTOs) -------------------

export type WatchpointType =
  | 'http'
  | 'https'
  | 'tcp'
  | 'dns'
  | 'tls'
  | 'elastic'
  | 'brobe'
  | 'waap'
  | 'delivery';

/** Protocol monitors take a config form; data monitors are query-driven. */
export const PROTOCOL_TYPES: WatchpointType[] = ['http', 'https', 'tcp', 'dns', 'tls'];
export const DATA_TYPES: WatchpointType[] = ['elastic', 'brobe', 'waap', 'delivery'];

export type WatchpointStatus = 'up' | 'down';

export interface Watchpoint {
  id: string;
  project_id: string;
  name: string;
  type: WatchpointType;
  config: Record<string, unknown>;
  check_interval: number;
  timeout_seconds: number;
  is_active: boolean;
  last_check_at?: string | null;
  last_status?: WatchpointStatus | string | null;
  last_latency_ms?: number | null;
  trigger_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProbeResult {
  id: string;
  watchpoint_id: string;
  timestamp: string;
  status_code?: number | null;
  latency_ms: number;
  is_up: boolean;
  error_message?: string | null;
  region: string;
  metadata?: Record<string, unknown> | null;
}

/** Uptime aggregate (`GET /watchpoints/:id/stats`). Numerics may arrive as strings. */
export interface UptimeStats {
  total_checks: number;
  success_count: number;
  failure_count: number;
  uptime_percent: number | string | null;
  avg_latency_ms: number | string | null;
  min_latency_ms: number | string | null;
  max_latency_ms: number | string | null;
}

/** One metric's 7-day baseline + suggested fixed thresholds (mean+2σ / mean+3σ). */
export interface BaselineMetric {
  metric_key: string; // 'count' (elastic) | 'latency_ms' (simple)
  mean: number;
  stddev: number;
  p95: number | null;
  sample_count: number;
  window_days: number;
  computed_at: string;
  suggested: { warning: number; critical: number };
}

/** Which (hour, day-class) baseline buckets exist for one metric. Hours are UTC
 * time_buckets (0..23). Uncovered hours fall back to the flat baseline at eval
 * time, where DROP detection is suppressed (trustDrop). */
export interface BaselineMetricCoverage {
  metric_key: string;
  status: 'none' | 'flat_only' | 'partial' | 'full';
  flat?: { sample_count: number } | null;
  hours_pooled: number[];
  hours_weekday: number[];
  hours_weekend: number[];
  uncovered_hours_utc: number[];
  drop_suppressed_hours: number[];
}

/** Baseline health block: per-metric seasonal coverage (added 1.4.000). */
export interface BaselineCoverage {
  computed_at: string;
  min_samples: number;
  metrics: BaselineMetricCoverage[];
}

/** `GET /watchpoints/:id/baseline` — status 'insufficient_data' when no baseline yet. */
export interface BaselineResponse {
  watchpoint_id: string;
  status: 'ok' | 'insufficient_data';
  metrics: BaselineMetric[];
  coverage?: BaselineCoverage | null;
}

export type AlarmSeverity = 'info' | 'warning' | 'critical';

export interface Alarm {
  id: string;
  watchpoint_id: string;
  playbook_id?: string | null;
  name: string;
  severity: AlarmSeverity;
  conditions: Record<string, unknown>;
  consecutive_failures: number;
  cooldown_minutes: number;
  is_active: boolean;
  /** When true, channels are notified once this alarm clears ("Recovered: <name>"). */
  notify_on_recovery: boolean;
  created_at: string;
  updated_at: string;
}

export type AlarmEventStatus = 'triggered' | 'acknowledged' | 'resolved';

export interface AlarmEvent {
  id: string;
  alarm_id: string;
  watchpoint_id: string;
  status: AlarmEventStatus;
  /** Denormalized from the alarm (since migration 000017) — no alarm join needed. */
  severity?: AlarmSeverity;
  alarm_name?: string;
  triggered_at: string;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  resolved_at?: string | null;
  /** "auto-recovery" when a data-monitor incident self-resolves. */
  resolved_by?: string | null;
  details?: Record<string, unknown> | null;
  /** Violating data_items subset for data monitors (elastic/waap/delivery); null for simple monitors. */
  matched_items?: Record<string, unknown>[] | null;
  created_at: string;
}

export interface AlarmEventCount {
  total: number;
  triggered: number;
}

// ---- Config shapes (per watchpoint type) ---------------------------------

export interface HttpConfig {
  url: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string | null;
  expected_status?: number;
  expected_body_contains?: string;
}
export interface TcpConfig {
  host: string;
  port: number;
}
export interface DnsConfig {
  host: string;
  record_type?: string;
  expected_values?: string[];
  nameserver?: string;
}
export interface TlsConfig {
  host: string;
  port?: number;
  expiry_threshold_days?: number;
  verify_hostname?: boolean;
}
export interface ElasticConfig {
  query: string;
  domains?: string[];
  threshold_interval?: number;
  dimensions?: string[];
  metric?: string;
  metric_field?: string | null;
  filters?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}
export interface WaapDeliveryConfig {
  customer_id: string;
  dashboard?: string;                 // legacy: fetch its whole batch when metrics is empty (waap only)
  metrics?: string[];                 // specific metric keys to fetch (auto-resolves dashboard); empty = all
  domains?: string[];
  mode?: string;
  time_range?: string;
}

// ---- Notification channel types (json mirrors BE dto.Channel) ------------

export type ChannelType = 'telegram' | 'email' | 'slack' | 'teams' | 'webhook';

/** Tenant-scoped notification channel. `type=null` means multi-type channel. */
export interface Channel {
  id: string;
  tenant_id: string;
  name: string;
  type: ChannelType | null;           // null = multi-type
  config: Record<string, unknown>;    // provider-specific (see phase-01)
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type SubscriberScope = 'telegram' | 'email' | 'webhook' | 'teams' | 'slack';
export type SubscriberStatus = 'pending' | 'verified' | 'disabled';

/** Single subscription endpoint within a channel. */
export interface Subscriber {
  id: string;
  channel_id: string;
  scope: SubscriberScope;
  key: string;                        // chat_id | email | webhook_url per scope
  name?: string | null;
  status: SubscriberStatus;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ---- Alarm field catalog (drives the condition builder, phase-02) ---------

export type FieldKind = 'string' | 'number' | 'bool';
export type EvalLevel = 'probe_result' | 'data_item';

/** One field exposed by the alarm catalog endpoint. */
export interface CatalogField {
  name: string;
  kind: FieldKind;
  operators: string[];
}

/** Full catalog for one watchpoint type (`GET /alarm-fields/:type`). */
export interface WatchpointFieldCatalog {
  type: WatchpointType;
  eval_level: EvalLevel;
  dimensions?: string[];              // group-by, elastic/data_item only
  condition_fields: CatalogField[];
  metrics?: string[];                 // selectable data_item metric keys (waap/delivery) — drives the metric picker
  metric_groups?: MetricGroup[];      // selectable metrics clustered by source dashboard (grouped picker)
  breakdown_metrics?: string[];       // subset of metrics that are per-entity breakdowns (exclusive select, one at a time)
  metric_catalog?: MetricInfo[];      // self-describing metric list; waap+delivery only; drives BI merged picker
}

/** One dashboard's selectable metrics, for the grouped create picker. */
export interface MetricGroup {
  dashboard: string;                  // registry key (ai_waf, security_overview, …)
  label: string;                      // group header
  metrics: string[];
}

/**
 * Self-describing metric entry returned from the backend metric_catalog.
 * Only waap and delivery catalogs include this. Use it to drive the merged
 * WAAP / BI picker without coupling the UI to internal dashboard registry keys.
 */
export interface MetricInfo {
  key: string;
  label: string;
  kind: 'scalar' | 'breakdown' | 'timeseries';
  unit: 'count' | 'bytes' | 'bytes_per_sec' | 'ratio' | 'rps';
  type: 'waap' | 'delivery';
  dashboard: string;
  category: string;
}

// ---- Playbook types (json mirrors BE dto.Playbook) -----------------------

export type PlaybookActionType = 'swift_blacklist' | 'alibaba_blacklist' | 'notify_oncall' | 'check_linked_services' | 'send_webhook';

/** Single remediation action within a playbook. */
export interface PlaybookAction {
  type: PlaybookActionType;
  config: Record<string, unknown>;    // per-action config (see phase-03)
  delay: number;                      // seconds before this action runs (int32)
}

/** Project-scoped auto-remediation playbook. */
export interface Playbook {
  id: string;
  project_id: string;
  name: string;
  actions: PlaybookAction[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ---- Query keys ----------------------------------------------------------

export const hcKeys = {
  all: ['hc'] as const,
  workspace: ['hc', 'workspace'] as const,
  watchpoints: (projectId: string, q?: Record<string, unknown>) =>
    ['hc', projectId, 'watchpoints', toQuery(q)] as const,
  watchpoint: (id: string) => ['hc', 'watchpoint', id] as const,
  watchpointStats: (id: string, q?: Record<string, unknown>) =>
    ['hc', 'watchpoint', id, 'stats', toQuery(q)] as const,
  watchpointMetrics: (id: string, q?: Record<string, unknown>) =>
    ['hc', 'watchpoint', id, 'metrics', toQuery(q)] as const,
  watchpointBaseline: (id: string) => ['hc', 'watchpoint', id, 'baseline'] as const,
  watchpointAlarms: (id: string) => ['hc', 'watchpoint', id, 'alarms'] as const,
  incidents: (q?: Record<string, unknown>) => ['hc', 'incidents', toQuery(q)] as const,
  incidentCount: () => ['hc', 'incidents', 'count'] as const,
  // ---- Phase 1-3 additions -----------------------------------------------
  channels: (q?: Record<string, unknown>) => ['hc', 'channels', toQuery(q)] as const,
  channel: (id: string) => ['hc', 'channel', id] as const,
  subscribers: (channelId: string) => ['hc', 'channel', channelId, 'subscribers'] as const,
  alarms: (q?: Record<string, unknown>) => ['hc', 'alarms', toQuery(q)] as const,
  alarm: (id: string) => ['hc', 'alarm', id] as const,
  alarmCatalog: (type?: string) => ['hc', 'alarm-fields', type ?? 'all'] as const,
  playbooks: (q?: Record<string, unknown>) => ['hc', 'playbooks', toQuery(q)] as const,
  playbook: (id: string) => ['hc', 'playbook', id] as const,
} as const;
